import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { getChartColors } from '@ovComponents/2-component/pieChart/getChartColors';
import { Selector } from '@ovComponents/2-component/selector/selector';
import {
  type ActiveOrganizationProps,
  UserContext,
  useFeatureFlag,
} from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import { Box, Typography } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryBuilder } from '../utils';
import { AumExplorerBar } from './aumExplorerBar';
import { AumExplorerPie } from './aumExplorerPie';

export type AumExplorerGroupByTypes =
  | 'accountType'
  | 'modelPortfolio'
  | 'childOrganization'
  | 'repCode';

export const AumExplorer = ({
  options,
}: {
  options: {
    customTitle: TranslatedString;
    display: 'pie' | 'bar';
    accountType: boolean;
    modelPortfolio: boolean;
    repCode: boolean;
    childOrganization: boolean;
  };
}) => {
  const {
    customTitle,
    accountType,
    modelPortfolio,
    repCode,
    childOrganization,
    display,
  } = options;
  const { activeOrganization } = useContext(UserContext);
  const { t } = useTranslation(['homePageConfiguration', 'accountTypes']);
  const repcodeAccessEnabled = useFeatureFlag(FlagsmithFlags.repcode_access);
  const [groupBy, setGroupBy] = useState<AumExplorerGroupByTypes>(() => {
    switch (true) {
      case accountType:
        return 'accountType';
      case modelPortfolio:
        return 'modelPortfolio';
      case childOrganization:
        return 'childOrganization';
      case repCode && repcodeAccessEnabled:
        return 'repCode';
      default:
        return 'accountType';
    }
  });
  const [totalValue, setTotalValue] = useState<number>(0);
  const [sData, setSData] = useState<any[]>([]);

  const getQuery = (
    groupBy: string,
    activeOrganization: ActiveOrganizationProps,
  ) => {
    let field = '';
    switch (groupBy) {
      case 'accountType':
        field = 'accountType';
        break;
      case 'modelPortfolio':
        field = 'financialProductName';
        break;
      case 'childOrganization':
        field = 'organizationName';
        break;
      case 'repCode':
        field = 'accountRepCode';
        break;
      default:
        field = 'accountType';
        break;
    }

    return {
      filters: [
        {
          field: 'organization',
          comparison: 'EQUALS',
          value: activeOrganization.id,
        },
        {
          field: 'state',
          comparison: 'EQUALS',
          value: 'ACTIVE',
        },
        {
          field: 'marketValueCentsInOrgCurrency',
          comparison: 'GREATER_THAN',
          value: '0',
        },
      ],
      type: 'SUB_ACCOUNT',
      grouping: {
        field,
        interval: null,
        type: 'GROUPING',
        valueAggregation: 'SUM',
        valueField: 'marketValueCentsInOrgCurrency',
      },
    };
  };

  const { data, loading } = useQueryBuilder(
    getQuery(groupBy, activeOrganization),
  );

  const colors = getChartColors();
  const sortedData = () => {
    if (loading) return;
    const d =
      data.generateReport?.grouping
        .filter((x: any) => x.value > 0)
        .sort((a: any, b: any) => b.value - a.value)
        .map((x: any) => ({
          ...x,
          key: groupBy === 'accountType' ? t(`accountTypes:${x.key}`) : x.key,
        })) ?? [];
    if (d.length > 7) {
      const topSix = d.slice(0, 6);
      const otherItems = d.slice(6);
      const otherValue = otherItems.reduce(
        (acc: number, curr: any) => acc + curr.value,
        0,
      );

      setSData([
        ...topSix,
        {
          key: t('other'),
          value: otherValue,
        },
      ]);
      return;
    }
    setSData(d);
  };

  useEffect(() => {
    if (data?.generateReport?.grouping) {
      setTotalValue(
        data?.generateReport.grouping.reduce(
          (acc: number, curr: any) => acc + curr.value,
          0,
        ),
      );
      sortedData();
    }
  }, [data, activeOrganization]);

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent sx={{ paddingBottom: '16px! important' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography variant='titleLarge'>
            {translateBackend(customTitle)}
          </Typography>
          <Box>
            <Selector
              onChange={(e: any) =>
                setGroupBy(e.target.value as AumExplorerGroupByTypes)
              }
              selectedOptionType={groupBy}
              options={
                Object.entries({
                  accountType,
                  modelPortfolio,
                  childOrganization,
                  ...(repcodeAccessEnabled ? { repCode } : {}),
                })
                  .filter(([_, value]) => value)
                  .map(([key, _]) => ({
                    type: key,
                    label: t(`aumExplorer.${key}`),
                    dataTestId: `aum-explorer-selection-${kebabCase(key)}`,
                  })) ?? []
              }
            />
          </Box>
        </Box>
        {display === 'pie' ? (
          <AumExplorerPie
            loading={loading}
            sData={sData}
            totalValue={totalValue}
            groupBy={groupBy}
            colors={colors}
          />
        ) : (
          <AumExplorerBar
            loading={loading}
            sData={sData}
            totalValue={totalValue}
            groupBy={groupBy}
            colors={colors}
          />
        )}
      </CardContent>
    </Card>
  );
};
